import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, Minus, ShoppingCart, User, MapPin, Utensils, Clock, X, AlertTriangle, WifiOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useOfflineCategories } from '@/hooks/useOfflineCategories';
import { useOfflineMenuItems, useOfflineAllMenuItems } from '@/hooks/useOfflineMenuItems';
import { useDeliveryZones } from '@/hooks/useDeliveryZones';
import { useMonthlyOrdersUsage } from '@/hooks/useMonthlyOrdersUsage';
import { useNetwork } from '@/contexts/NetworkContext';
import { useOfflineOrders } from '@/hooks/useOfflineOrders';
import { getCustomerId } from '@/utils/customerIdentifier';
import { OrderType, MenuItem, DeliveryZone } from '@/types/index';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/utils/currencyUtils';

interface OrderItem {
  menuItem: MenuItem;
  variant?: { id: string; size_name: string; price: number };
  quantity: number;
  notes?: string;
}

interface CreateManualOrderDialogProps {
  branchId: string;
  currency?: string;
  onOrderCreated: () => void;
  onOpenChange?: (open: boolean) => void;
}

interface CustomerInfo {
  name: string;
  phone: string;
  email: string;
  address: string;
  tableNumber: string;
  notes: string;
}

const initialCustomerInfo: CustomerInfo = {
  name: '',
  phone: '',
  email: '',
  address: '',
  tableNumber: '',
  notes: ''
};

export const CreateManualOrderDialog: React.FC<CreateManualOrderDialogProps> = ({
  branchId,
  currency = 'USD',
  onOrderCreated,
  onOpenChange
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    onOpenChange?.(newOpen)
  }
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>(initialCustomerInfo);
  const [orderType, setOrderType] = useState<OrderType>('pickup');
  const [selectedDeliveryZone, setSelectedDeliveryZone] = useState<DeliveryZone | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [activeTab, setActiveTab] = useState("customer");

  const { toast } = useToast();
  const { isOnline, isElectronApp, prefetchMenu } = useNetwork();
  const { categories, loading: categoriesLoading, isOffline: categoriesOffline, refetch: refetchCategories } = useOfflineCategories(branchId);
  const { menuItems, loading: menuItemsLoading, isOffline: menuItemsOffline } = useOfflineMenuItems(selectedCategory, branchId);
  const { menuItems: allMenuItems, loading: allMenuItemsLoading, isOffline: allMenuItemsOffline, refetch: refetchAllMenuItems } = useOfflineAllMenuItems(branchId);
  const { deliveryZones, loading: zonesLoading } = useDeliveryZones(branchId);
  
  // Offline-aware order creation
  const { createOrder, isOffline: ordersOffline } = useOfflineOrders(branchId);
  
  // Combined offline status
  const offlineFlags = useMemo(() => [categoriesOffline, menuItemsOffline, ordersOffline, allMenuItemsOffline], [allMenuItemsOffline, categoriesOffline, menuItemsOffline, ordersOffline]);
  const isOfflineMode = useMemo(() => offlineFlags.some(Boolean), [offlineFlags]);
  const hasCachedMenuData = categories.length > 0 && allMenuItems.length > 0;
  const isCheckingCache = isOfflineMode && allMenuItemsLoading;
  const offlineCacheMissing = isOfflineMode && !isCheckingCache && !hasCachedMenuData;
  const [isPrefetching, setIsPrefetching] = useState(false);
  
  // Monthly orders usage check
  const { current: monthlyOrdersUsed, limit: monthlyOrdersLimit, isAtLimit, isNearLimit } = useMonthlyOrdersUsage();

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setCustomerInfo(initialCustomerInfo);
      setOrderType('pickup');
      setSelectedDeliveryZone(null);
      setOrderItems([]);
      setSelectedCategory('');
      setActiveTab("customer");
    }
  }, [open]);

  // Auto-select first category when categories load
  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0].id);
    }
  }, [categories, selectedCategory]);

  // Calculate totals
  const resolvedCurrency = currency || 'USD';

  const subtotal = orderItems.reduce((sum, item) => {
    const price = item.variant?.price || item.menuItem.base_price;
    return sum + (price * item.quantity);
  }, 0);

  const deliveryFee = orderType === 'delivery' && selectedDeliveryZone ? selectedDeliveryZone.delivery_fee : 0;
  const total = subtotal + deliveryFee;

  // Validation
  const isFormValid = useMemo(() => {
    const hasItems = orderItems.length > 0;
    const hasTableNumber = orderType !== 'dine_in' || customerInfo.tableNumber.trim();
    const hasDeliveryInfo = orderType !== 'delivery' || (selectedDeliveryZone && customerInfo.address.trim());
    const meetsMinimumOrder = orderType !== 'delivery' || !selectedDeliveryZone || subtotal >= selectedDeliveryZone.minimum_order_amount;

    return hasItems && hasTableNumber && hasDeliveryInfo && meetsMinimumOrder;
  }, [customerInfo, orderItems, orderType, selectedDeliveryZone, subtotal]);

  const addItemToOrder = (menuItem: MenuItem, variant?: { id: string; size_name: string; price: number }) => {
    const existingItemIndex = orderItems.findIndex(item =>
      item.menuItem.id === menuItem.id &&
      item.variant?.id === variant?.id
    );

    if (existingItemIndex >= 0) {
      const updatedItems = [...orderItems];
      updatedItems[existingItemIndex].quantity += 1;
      setOrderItems(updatedItems);
    } else {
      setOrderItems(prev => [...prev, {
        menuItem,
        variant,
        quantity: 1,
        notes: ''
      }]);
    }
  };

  const updateItemQuantity = (index: number, quantity: number) => {
    if (quantity <= 0) {
      setOrderItems(prev => prev.filter((_, i) => i !== index));
    } else {
      setOrderItems(prev => prev.map((item, i) =>
        i === index ? { ...item, quantity } : item
      ));
    }
  };

  const updateItemNotes = (index: number, notes: string) => {
    setOrderItems(prev => prev.map((item, i) =>
      i === index ? { ...item, notes } : item
    ));
  };

  const removeItem = (index: number) => {
    setOrderItems(prev => prev.filter((_, i) => i !== index));
  };

  const removeItemFromOrder = (menuItem: MenuItem, variant?: { id: string; size_name: string; price: number }) => {
    setOrderItems(prev => prev.filter(item => !(
      item.menuItem.id === menuItem.id &&
      item.variant?.id === variant?.id
    )));
  };

  const isItemInOrder = (menuItem: MenuItem, variant?: { id: string; size_name: string; price: number }) => {
    return orderItems.some(item =>
      item.menuItem.id === menuItem.id &&
      item.variant?.id === variant?.id
    );
  };

  const handleSubmitOrder = async () => {
    if (!isFormValid) return;

    setIsSubmitting(true);
    try {
      const customerId = getCustomerId();

      const orderData = {
        branchId,
        customerName: customerInfo.name.trim() || 'Walk-in Customer',
        customerPhone: customerInfo.phone.trim() || '',
        customerEmail: customerInfo.email || undefined,
        customerAddress: customerInfo.address || undefined,
        notes: customerInfo.notes,
        customerId,
        orderType,
        tableNumber: orderType === 'dine_in' ? customerInfo.tableNumber : undefined,
        deliveryZoneId: orderType === 'delivery' ? selectedDeliveryZone?.id : undefined,
        deliveryFee,
        items: orderItems.map(item => ({
          menuItemId: item.menuItem.id,
          variantId: item.variant?.id,
          name: item.menuItem.name, // Required for offline
          quantity: item.quantity,
          unitPrice: item.variant?.price || item.menuItem.base_price,
          notes: item.notes || '',
        })),
      };

      // Use offline-aware order creation
      const result = await createOrder(orderData);
      
      if (!result.success) {
        if (result.error === 'offline.menu.notCached') {
          toast({
            title: t('offline.menuNotCachedTitle'),
            description: t('offline.menuNotCachedToast'),
            variant: 'destructive',
          });
          setActiveTab('menu');
          return;
        }

        throw new Error(result.error || 'Failed to create order');
      }

      const successMessage = result.isOffline 
        ? `تم إنشاء الطلب محلياً (سيتم المزامنة عند الاتصال)` 
        : `رقم الطلب: #${result.order?.order_number || 'N/A'} - ${customerInfo.name}`;

      toast({
        title: result.isOffline ? "📴 تم إنشاء الطلب (غير متصل)" : "✅ تم إنشاء الطلب بنجاح",
        description: successMessage,
        className: result.isOffline ? "bg-yellow-50 border-yellow-200 text-yellow-800" : "bg-green-50 border-green-200 text-green-800",
        duration: 3000
      });

      setOpen(false);
      // Delay callback to allow optimistic updates to settle
      setTimeout(() => {
        onOrderCreated();
      }, 100);
    } catch (error) {
      console.error('Error creating manual order:', error);
      toast({
        title: t('errors.genericTitle', 'Error Creating Order'),
        description: error instanceof Error ? error.message : t('errors.genericDescription', 'Failed to create order. Please try again.'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (price: number) => formatCurrency(price, resolvedCurrency);

  const handlePrefetchMenu = async () => {
    if (!branchId || !isElectronApp) return;
    setIsPrefetching(true);
    try {
      const result = await prefetchMenu(branchId);
      if (!result?.success) {
        toast({
          title: t('offline.menuPrefetchErrorTitle'),
          description: result?.error || t('offline.menuPrefetchError'),
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: t('offline.menuPrefetchSuccessTitle'),
        description: t('offline.menuPrefetchSuccess'),
      });

      await Promise.all([refetchCategories(), refetchAllMenuItems()]);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('offline.menuPrefetchError');
      toast({
        title: t('offline.menuPrefetchErrorTitle'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsPrefetching(false);
    }
  };

  // Block order creation if at limit
  if (isAtLimit) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button disabled variant="outline">
            <AlertTriangle className="h-4 w-4 mr-2 text-destructive" />
            {t('subscription.order_limit_reached')}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">{t('subscription.order_limit_reached')}</DialogTitle>
            <DialogDescription>
              {t('subscription.order_limit_message', { current: monthlyOrdersUsed, limit: monthlyOrdersLimit })}
            </DialogDescription>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {t('subscription.upgrade_to_continue')}
            </AlertDescription>
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Create Manual Order
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col max-md:fixed max-md:inset-0 max-md:w-full max-md:h-full max-md:max-w-none max-md:max-h-none max-md:rounded-none max-md:translate-x-0 max-md:translate-y-0 max-md:border-0">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-lg md:text-xl">Create Manual Order</DialogTitle>
          <DialogDescription className="text-xs md:text-sm">
            Create a new order manually for walk-in customers or phone orders
          </DialogDescription>
        </DialogHeader>

        {/* Order limit warning */}
        {isNearLimit && (
          <Alert className="bg-amber-50 border-amber-200 flex-shrink-0">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 text-xs md:text-sm">
              {t('subscription.order_limit_warning', { current: monthlyOrdersUsed, limit: monthlyOrdersLimit })}
            </AlertDescription>
          </Alert>
        )}

        {offlineCacheMissing && (
          <Alert variant="destructive" className="flex items-start gap-3 flex-shrink-0">
            <WifiOff className="h-4 w-4 mt-0.5" />
            <div className="flex-1 space-y-1">
              <p className="text-sm font-semibold">{t('offline.menuNotCachedTitle')}</p>
              <p className="text-xs text-muted-foreground">
                {t('offline.menuNotCachedDescription')}
              </p>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={handlePrefetchMenu}
                  disabled={!isOnline || isPrefetching}
                >
                  {isPrefetching ? t('offline.prefetching', 'Caching...') : t('offline.menuNotCachedAction')}
                </Button>
                {!isOnline && (
                  <span className="text-xs text-muted-foreground">{t('offline.menuNotCachedHint')}</span>
                )}
              </div>
            </div>
          </Alert>
        )}

        <div className="flex-1 overflow-hidden min-h-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
              <TabsTrigger value="customer" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-1 md:px-3">
                <User className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Customer Info</span>
                <span className="sm:hidden">Customer</span>
              </TabsTrigger>
              <TabsTrigger value="menu" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm px-1 md:px-3">
                <Utensils className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Menu Selection</span>
                <span className="sm:hidden">Menu</span>
              </TabsTrigger>
              <TabsTrigger value="review" className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                <span className="hidden sm:inline">Review & Submit</span>
                <span className="sm:hidden">Review</span>
                {orderItems.length > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {orderItems.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-hidden">
              <TabsContent value="customer" className="h-[60vh] md:h-[500px] mt-4">
                <ScrollArea className="h-full pr-4">
                  <div className="space-y-6 pb-20">
                    {/* Order Type Selection */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Order Type</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <RadioGroup value={orderType} onValueChange={(value) => setOrderType(value as OrderType)}>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="pickup" id="pickup" />
                            <Label htmlFor="pickup" className="flex items-center space-x-2 cursor-pointer">
                              <Utensils className="h-4 w-4" />
                              <span>Pickup</span>
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="delivery" id="delivery" />
                            <Label htmlFor="delivery" className="flex items-center space-x-2 cursor-pointer">
                              <MapPin className="h-4 w-4" />
                              <span>Delivery</span>
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="dine_in" id="dine_in" />
                            <Label htmlFor="dine_in" className="flex items-center space-x-2 cursor-pointer">
                              <Clock className="h-4 w-4" />
                              <span>Dine In</span>
                            </Label>
                          </div>
                        </RadioGroup>
                      </CardContent>
                    </Card>

                    {/* Customer Information */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Customer Information</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="customer-name">Name</Label>
                            <Input
                              id="customer-name"
                              value={customerInfo.name}
                              onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                              placeholder="Customer's full name (optional)"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="customer-phone">Phone</Label>
                            <Input
                              id="customer-phone"
                              value={customerInfo.phone}
                              onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                              placeholder="Customer's phone number (optional)"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="customer-email">Email</Label>
                          <Input
                            id="customer-email"
                            type="email"
                            value={customerInfo.email}
                            onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="Customer's email address"
                          />
                        </div>

                        {/* Conditional fields based on order type */}
                        {orderType === 'delivery' && (
                          <>
                            <div className="space-y-2">
                              <Label htmlFor="customer-address">Delivery Address *</Label>
                              <Input
                                id="customer-address"
                                value={customerInfo.address}
                                onChange={(e) => setCustomerInfo(prev => ({ ...prev, address: e.target.value }))}
                                placeholder="Customer's delivery address"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="delivery-zone">Delivery Zone *</Label>
                              {zonesLoading ? (
                                <div className="text-sm text-gray-500">Loading delivery zones...</div>
                              ) : deliveryZones.length > 0 ? (
                                <Select value={selectedDeliveryZone?.id || ''} onValueChange={(value) => {
                                  const zone = deliveryZones.find(z => z.id === value);
                                  setSelectedDeliveryZone(zone || null);
                                }}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select delivery zone" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {deliveryZones.map(zone => (
                                      <SelectItem key={zone.id} value={zone.id}>
                                        <div className="flex justify-between items-center w-full">
                                          <span>{zone.zone_name}</span>
                                          <span className="text-sm text-gray-500 ml-4">
                                            Fee: {formatPrice(zone.delivery_fee)} · Min: {formatPrice(zone.minimum_order_amount)}
                                          </span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div className="text-sm text-red-500">No delivery zones available</div>
                              )}
                            </div>
                          </>
                        )}

                        {orderType === 'dine_in' && (
                          <div className="space-y-2">
                            <Label htmlFor="table-number">Table Number *</Label>
                            <Input
                              id="table-number"
                              value={customerInfo.tableNumber}
                              onChange={(e) => setCustomerInfo(prev => ({ ...prev, tableNumber: e.target.value }))}
                              placeholder="Table number"
                            />
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label htmlFor="order-notes">Special Notes</Label>
                          <Textarea
                            id="order-notes"
                            value={customerInfo.notes}
                            onChange={(e) => setCustomerInfo(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Any special requests or notes"
                            rows={3}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="menu" className="mt-4">
                <div className="flex flex-col gap-3 h-[450px] md:h-[500px]">
                  {/* Mobile: Horizontal scrollable category chips */}
                  <div className="md:hidden">
                    <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide">
                      {categoriesLoading ? (
                        <div className="text-xs text-gray-500 px-3 py-2">Loading...</div>
                      ) : (
                        categories.map((category) => (
                          <button
                            key={category.id}
                            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${
                              selectedCategory === category.id 
                                ? 'bg-primary text-primary-foreground border-primary' 
                                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                            }`}
                            onClick={() => setSelectedCategory(category.id)}
                          >
                            {category.name}
                          </button>
                        ))
                      )}
                    </div>
                    {/* Real-time total display on mobile */}
                    {orderItems.length > 0 && (
                      <div className="bg-green-100 px-3 py-1.5 rounded-lg border border-green-300 text-center mt-2">
                        <span className="text-xs font-semibold text-green-800">
                          Total: {formatPrice(total)} ({orderItems.length} items)
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Desktop: Side-by-side layout */}
                  <div className="hidden md:flex gap-4 flex-1 min-h-0">
                    {/* Categories Sidebar */}
                    <div className="w-1/4 min-w-[180px]">
                      <Card className="h-full">
                        <CardHeader className="py-3">
                          <CardTitle className="text-base">Categories</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                          <ScrollArea className="h-[350px]">
                            {categoriesLoading ? (
                              <div className="p-4 text-sm text-gray-500">Loading categories...</div>
                            ) : (
                              <div className="space-y-1 p-3 pb-20">
                                {categories.map((category) => (
                                  <Button
                                    key={category.id}
                                    variant={selectedCategory === category.id ? "default" : "ghost"}
                                    className="w-full justify-start text-left text-sm h-9"
                                    onClick={() => setSelectedCategory(category.id)}
                                  >
                                    {category.name}
                                  </Button>
                                ))}
                              </div>
                            )}
                          </ScrollArea>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Menu Items - Desktop */}
                    <div className="flex-1">
                      <Card className="h-full">
                        <CardHeader className="pb-2 py-3">
                          <div className="flex justify-between items-center">
                            <CardTitle className="text-base">Menu Items</CardTitle>
                            {orderItems.length > 0 && (
                              <div className="bg-green-100 px-3 py-1 rounded-lg border border-green-300">
                                <span className="text-sm font-semibold text-green-800">
                                  Total: {formatPrice(total)} ({orderItems.length} items)
                                </span>
                              </div>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="p-3">
                          <ScrollArea className="h-[350px]">
                            {menuItemsLoading ? (
                              <div className="text-sm text-gray-500 p-4">Loading menu items...</div>
                            ) : menuItems.length === 0 ? (
                              <div className="text-sm text-gray-500 p-4">No items in this category</div>
                            ) : (
                              <div className="space-y-3 pb-20">
                                {menuItems.map((item) => {
                                  const totalQuantity = orderItems
                                    .filter(orderItem => orderItem.menuItem.id === item.id)
                                    .reduce((sum, orderItem) => sum + orderItem.quantity, 0);

                                  return (
                                    <Card key={item.id} className={`border ${totalQuantity > 0 ? 'bg-blue-50 border-blue-200' : ''}`}>
                                      <CardContent className="p-3">
                                        <div className="flex justify-between items-start">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                              <h4 className="font-medium text-sm">{item.name}</h4>
                                              {totalQuantity > 0 && (
                                                <Badge variant="secondary" className="text-xs">
                                                  {totalQuantity} in order
                                                </Badge>
                                              )}
                                            </div>
                                            {item.description && (
                                              <div className="text-xs text-gray-600 mt-1 line-clamp-1">{item.description}</div>
                                            )}
                                            <div className="mt-2">
                                              {item.variants && item.variants.length > 0 ? (
                                                <div className="space-y-1.5">
                                                  {item.variants.map((variant) => {
                                                    const variantQuantity = orderItems.find(orderItem =>
                                                      orderItem.menuItem.id === item.id && orderItem.variant?.id === variant.id
                                                    )?.quantity || 0;

                                                    return (
                                                      <div key={variant.id} className="flex justify-between items-center">
                                                        <div className="flex items-center gap-2">
                                                          <span className="text-xs">
                                                            {variant.size_name} - {formatPrice(variant.price)}
                                                          </span>
                                                          {variantQuantity > 0 && (
                                                            <Badge variant="outline" className="text-xs h-5">
                                                              {variantQuantity}
                                                            </Badge>
                                                          )}
                                                        </div>
                                                        <Button
                                                          size="sm"
                                                          className="h-7 w-7 p-0"
                                                          variant={isItemInOrder(item, variant) ? "destructive" : "default"}
                                                          onClick={() => {
                                                            if (isItemInOrder(item, variant)) {
                                                              removeItemFromOrder(item, variant);
                                                            } else {
                                                              addItemToOrder(item, variant);
                                                            }
                                                          }}
                                                        >
                                                          {isItemInOrder(item, variant) ? (
                                                            <Minus className="h-3 w-3" />
                                                          ) : (
                                                            <Plus className="h-3 w-3" />
                                                          )}
                                                        </Button>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              ) : (
                                                <div className="flex justify-between items-center">
                                                  <span className="font-medium text-sm">{formatPrice(item.base_price)}</span>
                                                  <Button
                                                    size="sm"
                                                    className="h-7 w-7 p-0"
                                                    variant={isItemInOrder(item) ? "destructive" : "default"}
                                                    onClick={() => {
                                                      if (isItemInOrder(item)) {
                                                        removeItemFromOrder(item);
                                                      } else {
                                                        addItemToOrder(item);
                                                      }
                                                    }}
                                                  >
                                                    {isItemInOrder(item) ? (
                                                      <Minus className="h-3 w-3" />
                                                    ) : (
                                                      <Plus className="h-3 w-3" />
                                                    )}
                                                  </Button>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  );
                                })}
                              </div>
                            )}
                          </ScrollArea>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Mobile: Menu Items List */}
                  <div className="md:hidden flex-1 min-h-0">
                    <ScrollArea className="h-full">
                      {menuItemsLoading ? (
                        <div className="text-sm text-gray-500 p-4 text-center">Loading menu items...</div>
                      ) : menuItems.length === 0 ? (
                        <div className="text-sm text-gray-500 p-4 text-center">No items in this category</div>
                      ) : (
                        <div className="space-y-2 pb-4">
                          {menuItems.map((item) => {
                            const totalQuantity = orderItems
                              .filter(orderItem => orderItem.menuItem.id === item.id)
                              .reduce((sum, orderItem) => sum + orderItem.quantity, 0);

                            return (
                              <div key={item.id} className={`border rounded-lg p-3 ${totalQuantity > 0 ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <h4 className="font-medium text-sm truncate">{item.name}</h4>
                                      {totalQuantity > 0 && (
                                        <Badge variant="secondary" className="text-xs flex-shrink-0">
                                          {totalQuantity}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-2">
                                  {item.variants && item.variants.length > 0 ? (
                                    <div className="space-y-1.5">
                                      {item.variants.map((variant) => {
                                        const variantQuantity = orderItems.find(orderItem =>
                                          orderItem.menuItem.id === item.id && orderItem.variant?.id === variant.id
                                        )?.quantity || 0;

                                        return (
                                          <div key={variant.id} className="flex justify-between items-center">
                                            <div className="flex items-center gap-1.5">
                                              <span className="text-xs text-gray-600">{variant.size_name}</span>
                                              <span className="text-xs font-medium">{formatPrice(variant.price)}</span>
                                              {variantQuantity > 0 && (
                                                <Badge variant="outline" className="text-[10px] h-4 px-1">
                                                  ×{variantQuantity}
                                                </Badge>
                                              )}
                                            </div>
                                            <Button
                                              size="sm"
                                              className="h-7 px-2"
                                              variant={isItemInOrder(item, variant) ? "destructive" : "default"}
                                              onClick={() => {
                                                if (isItemInOrder(item, variant)) {
                                                  removeItemFromOrder(item, variant);
                                                } else {
                                                  addItemToOrder(item, variant);
                                                }
                                              }}
                                            >
                                              {isItemInOrder(item, variant) ? (
                                                <Minus className="h-3 w-3" />
                                              ) : (
                                                <Plus className="h-3 w-3" />
                                              )}
                                            </Button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div className="flex justify-between items-center">
                                      <span className="font-medium text-sm">{formatPrice(item.base_price)}</span>
                                      <Button
                                        size="sm"
                                        className="h-7 px-2"
                                        variant={isItemInOrder(item) ? "destructive" : "default"}
                                        onClick={() => {
                                          if (isItemInOrder(item)) {
                                            removeItemFromOrder(item);
                                          } else {
                                            addItemToOrder(item);
                                          }
                                        }}
                                      >
                                        {isItemInOrder(item) ? (
                                          <Minus className="h-3 w-3" />
                                        ) : (
                                          <Plus className="h-3 w-3" />
                                        )}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="review" className="h-[60vh] md:h-[500px] mt-4">
                <ScrollArea className="h-full pr-2 md:pr-4">
                  <div className="space-y-4 md:space-y-6 pb-20">
                    {/* Order Summary */}
                    <Card>
                      <CardHeader className="py-3 md:py-6">
                        <CardTitle className="text-base md:text-lg">Order Summary</CardTitle>
                      </CardHeader>
                      <CardContent className="p-3 md:p-6 pt-0 md:pt-0">
                        {orderItems.length === 0 ? (
                          <div className="text-center py-6 md:py-8 text-gray-500">
                            <ShoppingCart className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-3 md:mb-4 text-gray-300" />
                            <p className="text-sm md:text-base">No items added to order yet</p>
                            <p className="text-xs md:text-sm">Go to Menu Selection to add items</p>
                          </div>
                        ) : (
                          <div className="space-y-3 md:space-y-4">
                            {orderItems.map((item, index) => (
                              <div key={`${item.menuItem.id || index}-${item.variant?.id || 'base'}`} className="flex items-start gap-2 md:gap-3 p-2 md:p-3 border rounded-lg">
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-sm md:text-base truncate">{item.menuItem.name}</h4>
                                  {item.variant && (
                                    <p className="text-xs md:text-sm text-gray-600">{item.variant.size_name}</p>
                                  )}
                                  <p className="text-xs md:text-sm font-medium">
                                    {formatPrice(item.variant?.price || item.menuItem.base_price)} × {item.quantity}
                                  </p>
                                  <div className="mt-2">
                                    <Input
                                      placeholder="Special notes for this item"
                                      value={item.notes || ''}
                                      onChange={(e) => updateItemNotes(index, e.target.value)}
                                      className="text-xs md:text-sm h-8 md:h-9"
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 w-7 md:h-8 md:w-8 p-0"
                                    onClick={() => updateItemQuantity(index, item.quantity - 1)}
                                  >
                                    <Minus className="h-3 w-3" />
                                  </Button>
                                  <span className="w-6 md:w-8 text-center text-sm">{item.quantity}</span>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 w-7 md:h-8 md:w-8 p-0"
                                    onClick={() => updateItemQuantity(index, item.quantity + 1)}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => removeItem(index)}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}

                            <Separator />

                            {/* Order Totals */}
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span>Subtotal:</span>
                                <span>{formatPrice(subtotal)}</span>
                              </div>
                              {deliveryFee > 0 && (
                                <div className="flex justify-between">
                                  <span>Delivery Fee:</span>
                                  <span>{formatPrice(deliveryFee)}</span>
                                </div>
                              )}
                              <div className="flex justify-between font-bold text-lg">
                                <span>Total:</span>
                                <span>{formatPrice(total)}</span>
                              </div>
                            </div>

                            {/* Minimum order warning */}
                            {orderType === 'delivery' && selectedDeliveryZone && subtotal < selectedDeliveryZone.minimum_order_amount && (
                              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-sm text-yellow-800">
                                  Minimum order amount for {selectedDeliveryZone.zone_name} is {formatPrice(selectedDeliveryZone.minimum_order_amount)}.
                                  Add {formatPrice(selectedDeliveryZone.minimum_order_amount - subtotal)} more to proceed.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Customer Details Summary */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Customer Details</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm">
                          <div><strong>Name:</strong> {customerInfo.name.trim() || 'Walk-in Customer'}</div>
                          <div><strong>Phone:</strong> {customerInfo.phone.trim() || 'Not provided'}</div>
                          {customerInfo.email && <div><strong>Email:</strong> {customerInfo.email}</div>}
                          <div><strong>Order Type:</strong> {orderType.replace('_', ' ').toUpperCase()}</div>
                          {orderType === 'delivery' && (
                            <>
                              <div><strong>Address:</strong> {customerInfo.address || 'Not provided'}</div>
                              <div><strong>Delivery Zone:</strong> {selectedDeliveryZone?.zone_name || 'Not selected'}</div>
                            </>
                          )}
                          {orderType === 'dine_in' && (
                            <div><strong>Table Number:</strong> {customerInfo.tableNumber || 'Not provided'}</div>
                          )}
                          {customerInfo.notes && <div><strong>Notes:</strong> {customerInfo.notes}</div>}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </ScrollArea>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t('actions.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmitOrder}
            disabled={!isFormValid || isSubmitting || offlineCacheMissing}
            className="min-w-[120px]"
          >
            {isSubmitting ? t('orders.actions.creating', 'Creating...') : t('orders.actions.create', 'Create Order')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 